import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/CalendarView.css';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';

const localizer = momentLocalizer(moment);

const LiveKitCalendarView = ({ user, activeAgency }) => {
  const modal = useModal();
  const { selectedOrganization, selectedBrand } = useOrgBrand();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const isSuperAdmin = user?.is_super_admin;
  const orgId = selectedOrganization?.id;
  const brandId = selectedBrand?.id;

  const fetchMeetings = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await axios.get('/api/livekit/meetings', {
        params: {
          organization_id: orgId,
          brand_id: brandId
        },
        withCredentials: true
      });

      const meetings = response.data.meetings || [];
      const now = new Date();

      const transformedEvents = meetings
        .filter(meeting => meeting.scheduled_start)
        .map(meeting => {
          const start = new Date(meeting.scheduled_start);
          const end = meeting.scheduled_end
            ? new Date(meeting.scheduled_end)
            : new Date(start.getTime() + 60 * 60 * 1000);

          return {
            id: meeting.meeting_id,
            title: meeting.title || 'Untitled Meeting',
            start,
            end,
            description: meeting.description || '',
            status: meeting.room_status,
            isPast: end < now,
          };
        });

      setEvents(transformedEvents);
      setError(null);
    } catch (err) {
      console.error('Error fetching LiveKit meetings:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view meetings.');
      } else {
        setError(err.response?.data?.error || 'Failed to load meetings');
      }
    } finally {
      setIsLoading(false);
    }
  }, [orgId, brandId]);

  useEffect(() => {
    if (isSuperAdmin && !activeAgency) {
      setIsLoading(false);
      return;
    }
    fetchMeetings();
  }, [fetchMeetings, isSuperAdmin, activeAgency]);

  const handleSelectEvent = useCallback((event) => {
    const details = [
      event.description,
      `Status: ${event.status || 'scheduled'}`,
      `${moment(event.start).format('MMM D, YYYY h:mma')} - ${moment(event.end).format('h:mma')}`
    ].filter(Boolean).join('\n\n');

    modal.alert(details, event.title);
  }, [modal]);

  const eventStyleGetter = (event) => {
    let backgroundColor = '#1a73e8';
    let opacity = 1;

    if (event.isPast) {
      backgroundColor = '#9e9e9e';
      opacity = 0.8;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  };

  if (error) {
    return (
      <div className="calendar-error">
        <h3>Unable to load meetings</h3>
        <p>{error}</p>
        <button onClick={() => fetchMeetings()} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="calendar-loading">
        <div className="loading-spinner"></div>
        <p>Loading your Velvet Meetings...</p>
      </div>
    );
  }

  const pastEventsCount = events.filter(e => e.isPast).length;
  const futureEventsCount = events.filter(e => !e.isPast).length;

  return (
    <div className="calendar-content">
      <div className="calendar-header">
        <div className="calendar-header-info">
          <h2>Velvet Meetings Calendar</h2>
          <p>
            Showing {events.length} meetings ({pastEventsCount} past, {futureEventsCount} upcoming)
          </p>
        </div>
        <div className="calendar-actions">
          <button onClick={() => fetchMeetings()} className="refresh-btn">
            Refresh Calendar
          </button>
        </div>
      </div>

      <div className="calendar-view-wrapper">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          views={['month']}
          defaultView="month"
          date={currentDate}
          onNavigate={setCurrentDate}
          popup
          showMultiDayTimes={false}
          tooltipAccessor={(event) => event.description || event.title}
          eventPropGetter={eventStyleGetter}
          dayLayoutAlgorithm='no-overlap'
          formats={{
            eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
              localizer.format(start, 'h:mma', culture) + ' - ' + localizer.format(end, 'h:mma', culture),
            timeGutterFormat: (date, culture, localizer) =>
              localizer.format(date, 'ha', culture),
            monthHeaderFormat: (date, culture, localizer) =>
              localizer.format(date, 'MMMM YYYY', culture),
          }}
        />
      </div>
    </div>
  );
};

export default LiveKitCalendarView;
